// Web Push subscription for Grit. The phone's subscription is stored in
// grit_push_subscriptions (through functions/api/db.js) and read by the grit-push
// Worker (push-worker/), which sends the Sunday "Set this week's focus" reminder.
import { sb } from './db.js';
import { activeUser } from './auth.js';

// Grit's own VAPID keypair, made 2026-10-15 (the old one was shared with the retired
// KLT Family Portal and its private half is gone). MUST match VAPID_PUBLIC_KEY in
// push-worker/wrangler.toml. Rotating it means every phone has to turn reminders on again.
const VAPID_PUBLIC_KEY = 'BJxLzI7VhiC_BvlZd8R9kJNY8Tgk482Vb6DyS2Tztwedhf2hvSxr5p1nmMgo39EzWEfo-DB6XPNX6X3g36FfJYM';

function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

function sameKey(buf, b64) {
  if (!buf) return false;
  const a = new Uint8Array(buf), b = urlB64ToUint8Array(b64);
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// True on iPhone/iPad. iPadOS 13+ reports as Mac, so also sniff touch + Mac platform.
export function isIOS() {
  const ua = navigator.userAgent || '';
  const iPhoneOrPad = /iPad|iPhone|iPod/.test(ua);
  const iPadOnMac = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
  return iPhoneOrPad || iPadOnMac;
}

// True when running as an installed Home Screen / standalone web app (not a Safari tab).
export function isStandalone() {
  return window.navigator.standalone === true
    || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
}

// Ask permission, subscribe this device, store the blob.
// Returns { ok, reason } so the UI can show the right message:
//   reason: 'ok' | 'ios-needs-install' | 'unsupported' | 'denied' | 'subscribe-failed'
export async function enablePush() {
  // iOS only exposes PushManager/Notification inside an installed Home Screen web app.
  // In a normal Safari tab these APIs are absent, so pushSupported() is false there.
  if (!pushSupported()) {
    if (isIOS() && !isStandalone()) return { ok: false, reason: 'ios-needs-install' };
    return { ok: false, reason: 'unsupported' };
  }
  let perm = Notification.permission;
  if (perm === 'default') perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    // On iOS a never-installed app can't even reach here, but cover the in-tab edge case.
    if (isIOS() && !isStandalone()) return { ok: false, reason: 'ios-needs-install' };
    return { ok: false, reason: 'denied' };
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    // A phone that turned reminders on before 2026-10-15 still holds a subscription made
    // with the old key, and sends signed with the new key would be refused. Start fresh.
    if (sub && !sameKey(sub.options?.applicationServerKey, VAPID_PUBLIC_KEY)) {
      await sub.unsubscribe().catch(() => {});
      sub = null;
    }
    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(VAPID_PUBLIC_KEY) });
    }
    const json = sub.toJSON();
    const user = activeUser();
    await sb.from('grit_push_subscriptions').delete().eq('user_id', user).eq('subscription->>endpoint', json.endpoint);
    const { error } = await sb.from('grit_push_subscriptions').insert({ user_id: user, subscription: json });
    if (error) return { ok: false, reason: 'subscribe-failed' };
    return { ok: true, reason: 'ok' };
  } catch (e) {
    console.warn('enablePush failed', e);
    return { ok: false, reason: 'subscribe-failed' };
  }
}

export async function disablePush() {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const json = sub.toJSON();
      await sb.from('grit_push_subscriptions').delete().eq('user_id', activeUser()).eq('subscription->>endpoint', json.endpoint);
      await sub.unsubscribe();
    }
  } catch { /* ignore */ }
}
